import { colorToCss, darken } from "../../color/color"
import { isUndefinedOrNull } from "../../utils/utils"
import { ol_Feature, ol_style, ol_geom } from "@omnia/exports/openlayers";


export interface IMapMarker {
    svg: string
    width: number
    height: number
}

export interface IMapMarkerInfo extends IMapMarker {
    imgSrc: string
}

export function getDefaultMapMarkerStyles(options: {
    color: number[],
    geometry: (feature: ol_Feature) => ol_geom.Geometry
    selected: boolean,
    scale?: number,
    dot?: boolean,
    dotColor?: number[],
    anchor?: number[]
}) {

    const marker = getDefaultMapMarker(options)

    const style = new ol_style.Style({
        image: new ol_style.Icon({
            opacity: 1,
            scale: options.scale || 1,
            anchor: options.anchor || [0.5, 1],
            src: getSvgAsDataUrl(marker.svg),
        }),
        geometry: options.geometry
    })

    return [style]

}

export function getDefaultMapMarker(options?: { color?: number[], selected?: boolean, dot?: boolean, dotColor?: number[] }): IMapMarkerInfo {

    options = options || {}

    const color = options.color ? options.color : [255, 255, 255]
    const selected = options.selected
    const dot = isUndefinedOrNull(options.dot) ? true : options.dot

    let marker = getDefaultMarkerDef(color, true)

    if (dot) {
        marker = getMarkerWithDotDef(
            marker,
            selected ? getSelectedDotStyle(options.dotColor) : dotStyle,
            !selected
        )
    }

    if (selected) {
        marker = applyDropShadowFilter({ ...marker })
    }

    const svg = `
            <svg
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
                width="${marker.width}"
                height="${marker.height}"
                style="overflow: visible"
            >
                ${marker.svg}
            </svg>
        `

    return {
        ...marker,
        svg,
        imgSrc: getSvgAsDataUrl(svg)
    }

}

function getDefaultMarkerDef(color: number[], stroke: boolean = true) {

    const outerColor = colorToCss(color)
    const innerColor = colorToCss(darken(color, 0.5))

    const scale = 0.8
    const transform = `transform="matrix(${scale} 0 0 ${scale} 0 0)"`

    const svg =
        `
            <g ${transform}>
                <path
                    d="m 20.000001,1.2501305 c -9.665032,0 -17.5000012,7.8349687 -17.5000012,17.5002185 0,3.108438 0.8347502,6.015406 2.25575,8.544375 0.2360312,0.419781 0.4834375,0.833219 0.7520625,1.231563 L 20.000001,53.75013 34.492188,28.526287 c 0.223136,-0.330532 0.415625,-0.680313 0.614688,-1.026157 l 0.137812,-0.205625 c 1.419688,-2.528968 2.255313,-5.435937 2.255313,-8.544375 0,-9.6652495 -7.835625,-17.5002183 -17.5,-17.5002183 z m 0,8.7502185 c 4.832187,0 8.75,3.917594 8.75,8.75 0,4.831969 -3.917813,8.749781 -8.75,8.749781 -4.832406,0 -8.75,-3.917812 -8.75,-8.749781 0,-4.832406 3.917594,-8.75 8.75,-8.75 z"
                    style="fill:${outerColor};${(stroke ? "stroke:#000000;stroke-width:1;stroke-linecap:round;stroke-linejoin:round;stroke-miterlimit:4;stroke-dasharray:none;stroke-opacity:1" : "")}" />
                <path
                    d="m 20.013889,7.7848492 c -6.040562,0 -10.9375003,4.8969378 -10.9375003,10.9374998 0,6.039688 4.8969383,10.9375 10.9375003,10.9375 6.039688,0 10.9375,-4.897812 10.9375,-10.9375 0,-6.040562 -4.897812,-10.9374998 -10.9375,-10.9374998 z m 0,4.3749998 c 3.624688,0 6.5625,2.938031 6.5625,6.5625 0,3.624469 -2.937812,6.5625 -6.5625,6.5625 -3.624687,0 -6.5625,-2.938031 -6.5625,-6.5625 0,-3.624469 2.937813,-6.5625 6.5625,-6.5625 z"
                    style="fill:${innerColor};stroke-width:2.1875" />
            </g>
        `

    const marker = {
        svg,
        width: 40 * scale,
        height: 55 * scale
    }

    return marker
}

function getMarkerWithDotDef(marker: IMapMarker, dotStyle: DotStyle, below: boolean) {

    const dot = getDotDef(dotStyle)

    const size = {
        width: marker.width,
        height: marker.height + (dot.height / 2)
    }

    const dotTranslation = {
        x: (size.width - dot.width) / 2,
        y: size.height - dot.height
    }

    const dotSvg = `<g transform="matrix(1 0 0 1 ${dotTranslation.x} ${dotTranslation.y})">${dot.svg}</g>`

    const svg = below ? `<g>${dotSvg}<g>${marker.svg}</g></g>` : `<g><g>${marker.svg}</g>${dotSvg}</g>`

    return {
        svg,
        ...size,
    }

}

function getDotDef(dotStyle: DotStyle) {

    const { outerFill, innerFill, stroke } = dotStyle

    const svg =
        `<circle cx="7" cy="7" r="6" fill="${outerFill}" stroke="${stroke}" stroke-width="1" />`
        + (
            innerFill
                ? `<circle cx="7" cy="7" r="4" fill="${innerFill}" />`
                : ""
        )

    const marker = {
        svg,
        width: 14,
        height: 14
    }

    return marker

}

function applyDropShadowFilter(content: IMapMarker) {

    const shadow = getDropShadowFilterDef({ width: content.width, height: content.height })

    const svg = `
        <g>
            <defs>
                ${shadow.svg}
            </defs>
            <g style="filter:url(#filterDropShadow)">
                ${content.svg}
            </g>
        </g>
        `

    return {
        ...shadow,
        svg
    }

}

function getDropShadowFilterDef(size: { width: number, height: number }) {

    const width = size.width + 5
    const height = size.height + 5

    const svg = `
        <filter
            width="${width}"
            height="${height}"
            style="color-interpolation-filters:sRGB;"
            id="filterDropShadow">
        <feFlood
            flood-opacity="0.498039"
            flood-color="rgb(0,0,0)"
            result="flood"
            id="feFlood837" />
        <feComposite
            in="flood"
            in2="SourceGraphic"
            operator="in"
            result="composite1"
            id="feComposite839" />
        <feGaussianBlur
            in="composite1"
            stdDeviation="2"
            result="blur"
            id="feGaussianBlur841" />
        <feOffset
            dx="3"
            dy="3"
            result="offset"
            id="feOffset843" />
        <feComposite
            in="SourceGraphic"
            in2="offset"
            operator="over"
            result="composite2"
            id="feComposite845" />
        </filter>
     `

    return {
        svg,
        width,
        height
    }

}

function getSelectedDotStyle(color?: number[]) {

    return {
        ...selectedDotStyle,
        innerFill: color ? colorToCss(color) : selectedDotStyle.innerFill
    }

}

function getSvgAsDataUrl(svg: string) {
    svg = encodeURIComponent(svg)
    svg = "data:image/svg+xml;charset=utf8," + svg
    return svg
}

const dotStyle = {
    stroke: "#000",
    outerFill: "#fff",
    innerFill: null
}

const selectedDotStyle = {
    stroke: "#000",
    outerFill: "#fff",
    innerFill: "#0099ff"
}

interface DotStyle {
    stroke: string
    outerFill: string
    innerFill: string
}
